import { useState, useEffect } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Checkbox,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  CircularProgress,
  AvatarGroup,
  Tooltip,
} from "@mui/material";
import {
  Add,
  EditOutlined,
  DeleteOutlined,
  ViewKanbanOutlined,
  ListAltOutlined,
  DashboardOutlined,
  PeopleOutlined,
  Lock,
  AdminPanelSettings,
  Visibility,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import TopAppBar from "../global/TopBar";
import AdvertSideBar from "../global/AdvertsSideBar";
import { useAuth } from "../../AuthContext";

// Import Refactored Modular Components
import TaskCard, { priorityConfig } from "./components/TaskCard";
import KanbanColumn from "./components/KanbanColumn";
import TaskDetailPanelMui from "./components/TaskDetailPanelMui";
import DashboardView from "./components/DashboardView";
import TeamView from "./components/TeamView";

// Graceful Mock Fallbacks
import { tasks as initialTaskData } from "./components/data/mockData.jsx";

const defaultColumns = [
  { id: "todo", label: "To Do" },
  { id: "in_progress", label: "In Progress" },
  { id: "completed", label: "Completed" },
  { id: "review", label: "Review" },
];

const selectSx = {
  fontSize: 13,
  borderRadius: "8px",
  bgcolor: "white",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.2)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6C5CE7" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6C5CE7",
    borderWidth: 1,
  },
};

const mapStaffToUser = (staff) => {
  if (!staff) return { id: "unassigned", name: "Unassigned", avatar: "U", email: "", department: "" };
  return {
    id: staff.Id || staff.id,
    name: staff.name || `${staff.FirstName || ""} ${staff.LastName || ""}`.trim(),
    email: staff.email || "",
    avatar: staff.avatar || `${(staff.FirstName || "?")[0]}${(staff.LastName || "?")[0]}`.toUpperCase(),
    department: staff.department || "General",
    image: staff.image || null,
  };
};

export default function TaskDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Real Backend Authentication Context
  const { userRole, userID, getData, addData, updateData, deleteData } = useAuth();
  
  // App States
  const [tasks, setTasks] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [boardPermissions, setBoardPermissions] = useState([]); // Outgoing
  const [incomingPermissions, setIncomingPermissions] = useState([]); // Incoming
  const [columns, setColumns] = useState(defaultColumns);
  const [tabValue, setTabValue] = useState(0);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all-priority");
  const [sortBy, setSortBy] = useState("due-date");
  
  // Panel/Dialog States
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  
  // Status States
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestFeedbackText, setRequestFeedbackText] = useState("");

  // Map currentUser from database staffs list using Auth ID
  const currentUser = mapStaffToUser(
    staffs.find((s) => s.Id === userID || s.id === userID)
  ) || {
    id: userID || "1",
    name: "You",
    avatar: "Y",
    email: "you@example.com",
    department: "Management",
  };

  // ========================
  // Relational Mapping Helpers
  // ========================
  const parseTask = (rawTask, staffsList) => {
    let subtasks = [];
    let comments = [];
    let attachments = [];
    let collaboratorIds = [];

    try {
      subtasks = typeof rawTask.subtasks === "string" ? JSON.parse(rawTask.subtasks) : rawTask.subtasks || [];
    } catch { subtasks = []; }
    try {
      comments = typeof rawTask.comments === "string" ? JSON.parse(rawTask.comments) : rawTask.comments || [];
    } catch { comments = []; }
    try {
      attachments = typeof rawTask.attachments === "string" ? JSON.parse(rawTask.attachments) : rawTask.attachments || [];
    } catch { attachments = []; }
    try {
      collaboratorIds = typeof rawTask.collaborators === "string" ? JSON.parse(rawTask.collaborators) : rawTask.collaborators || [];
    } catch { collaboratorIds = []; }

    // Resolve Assignee
    const assignedStaff = staffsList.find((s) => s.Id === rawTask.assignee_id || s.id === rawTask.assignee_id);
    const assignee = assignedStaff ? mapStaffToUser(assignedStaff) : { name: "Unassigned", avatar: "U", id: "unassigned" };

    // Resolve Collaborators
    const collaborators = collaboratorIds
      .map((cid) => staffsList.find((s) => s.Id === cid || s.id === cid))
      .filter(Boolean)
      .map(mapStaffToUser);

    return {
      id: rawTask.id || rawTask.Id,
      title: rawTask.title || "Untitled",
      description: rawTask.description || "",
      status: rawTask.status || "todo",
      priority: rawTask.priority || "medium",
      assignee,
      dueDate: rawTask.dueDate || "",
      createdDate: rawTask.createdDate || "",
      section: rawTask.section || "To Do",
      workspace: rawTask.workspace || "1",
      subtasks,
      comments,
      attachments,
      collaborators,
      timeEstimate: rawTask.timeEstimate || "",
      recurring: rawTask.recurring === "1" || rawTask.recurring === 1 || false,
      likes: parseInt(rawTask.likes || 0),
      completedSubtasks: parseInt(rawTask.completedSubtasks || 0),
      totalSubtasks: parseInt(rawTask.totalSubtasks || 0),
    };
  };

  const serializeTask = (taskData) => {
    return {
      title: taskData.title || "",
      description: taskData.description || "",
      status: taskData.status || "todo",
      priority: taskData.priority || "medium",
      assignee_id: taskData.assignee?.id || "unassigned",
      dueDate: taskData.dueDate || "",
      createdDate: taskData.createdDate || new Date().toISOString().split("T")[0],
      section: taskData.section || "To Do",
      workspace: taskData.workspace || "1",
      subtasks: JSON.stringify(taskData.subtasks || []),
      comments: JSON.stringify(taskData.comments || []),
      attachments: JSON.stringify(taskData.attachments || []),
      collaborators: JSON.stringify((taskData.collaborators || []).map((c) => c.id)),
      timeEstimate: taskData.timeEstimate || "",
      recurring: taskData.recurring ? 1 : 0,
      likes: taskData.likes || 0,
      completedSubtasks: taskData.completedSubtasks || 0,
      totalSubtasks: taskData.totalSubtasks || 0,
    };
  };

  // ========================
  // Data Fetching & Sync
  // ========================
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 1. Fetch Staff List
        const staffRes = await getData("proccess.php", "staffs");
        let staffsList = [];
        if (staffRes.success && Array.isArray(staffRes.data)) {
          setStaffs(staffRes.data);
          staffsList = staffRes.data;
        } else {
          console.error("Failed to load staffs from database");
        }

        // 2. Fetch Tasks (graceful fallback to mock tasks)
        try {
          const tasksRes = await getData("proccess.php", "tasks_V2");
          if (tasksRes.success && Array.isArray(tasksRes.data) && tasksRes.data.length > 0) {
            const parsed = tasksRes.data.map((t) => parseTask(t, staffsList));
            setTasks(parsed);
          } else {
            console.warn("tasks_V2 table empty or unavailable; seeding mock data.");
            const parsedMock = initialTaskData.map((t) => parseTask({
              ...t,
              assignee_id: t.assignee?.id || "unassigned",
              collaborators: JSON.stringify((t.collaborators || []).map((c) => c.id)),
              subtasks: JSON.stringify(t.subtasks || []),
              comments: JSON.stringify(t.comments || []),
              attachments: JSON.stringify(t.attachments || []),
            }, staffsList));
            setTasks(parsedMock);
          }
        } catch (tErr) {
          console.error("Error connecting to tasks_V2 table, falling back:", tErr);
          setTasks(initialTaskData.map((t) => parseTask({
            ...t,
            assignee_id: t.assignee?.id || "unassigned",
            collaborators: JSON.stringify((t.collaborators || []).map((c) => c.id)),
          }, staffsList)));
        }

        // 3. Fetch Permissions (graceful fallback to seed permissions)
        try {
          const permRes = await getData("proccess.php", "board_permissions_V2");
          let outgoing = [];
          let incoming = [];
          if (permRes.success && Array.isArray(permRes.data)) {
            const currentUserId = userID || "1";
            outgoing = permRes.data.filter((p) => p.owner_id === currentUserId);
            incoming = permRes.data.filter((p) => p.shared_with_id === currentUserId).map(p => ({
              owner_id: p.owner_id,
              permission: p.permission,
              hasAccess: p.permission !== "none",
              recordId: p.Id || p.id
            }));
          }
          
          setBoardPermissions(outgoing);
          
          if (incoming.length === 0) {
            // Seed incoming mock shares for rich evaluation
            setIncomingPermissions([
              { owner_id: "2", permission: "view", hasAccess: true },   // Bob Smith shares as Viewer
              { owner_id: "3", permission: "edit", hasAccess: true },   // Carol White shares as Editor
              { owner_id: "4", permission: "none", hasAccess: false },  // David Lee (No Access)
              { owner_id: "5", permission: "edit", hasAccess: true },   // Emma Davis shares as Editor
            ]);
          } else {
            setIncomingPermissions(incoming);
          }
        } catch (pErr) {
          console.error("Permissions table offline, loading mock permissions:", pErr);
          setIncomingPermissions([
            { owner_id: "2", permission: "view", hasAccess: true },
            { owner_id: "3", permission: "edit", hasAccess: true },
            { owner_id: "4", permission: "none", hasAccess: false },
            { owner_id: "5", permission: "edit", hasAccess: true },
          ]);
        }

      } catch (err) {
        console.error("Critical loader error:", err);
        setError("Failed to synchronize task manager with backend. Displaying offline fallback.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [userID]);

  // ========================
  // Permissions Evaluation Helper
  // ========================
  const getPermissionOnCurrentBoard = () => {
    // Admin override
    const adminRoles = ["hr", "admin", "supervisor", "manager", "supperAdmin", "ceo", "coo"];
    if (adminRoles.includes(userRole)) return "edit";

    // Global dashboard, unassigned or own board
    if (assigneeFilter === "all" || assigneeFilter === "my" || assigneeFilter === "unassigned" || assigneeFilter === currentUser.id) {
      return "edit";
    }

    // Checking incoming shared boards
    const sharedBoard = incomingPermissions.find((p) => p.owner_id === assigneeFilter);
    if (!sharedBoard || !sharedBoard.hasAccess) return "none"; // Access Denied
    return sharedBoard.permission; // 'view' or 'edit'
  };

  const activePermission = getPermissionOnCurrentBoard();
  const isBoardReadOnly = activePermission === "view";
  const isBoardLocked = activePermission === "none";

  const hasWriteAccess = (task) => {
    const adminRoles = ["hr", "admin", "supervisor", "manager", "supperAdmin", "ceo", "coo"];
    if (adminRoles.includes(userRole)) return true;
    
    // If viewing someone else's board, respect our board permission
    if (assigneeFilter !== "all" && assigneeFilter !== "my" && assigneeFilter !== currentUser.id) {
      return activePermission === "edit";
    }

    if (!task) return false;
    const isAssignee = task.assignee?.id === currentUser.id;
    const isCollaborator = Array.isArray(task.collaborators) && task.collaborators.some((c) => c.id === currentUser.id);
    return isAssignee || isCollaborator;
  };

  // ========================
  // Board Permissions Callbacks
  // ========================
  const handleGrantAccess = async (userId, hasAccess, permission) => {
    try {
      const existing = boardPermissions.find((p) => p.shared_with_id === userId);
      
      if (!hasAccess) {
        // Revoke
        if (existing) {
          const recordId = existing.Id || existing.id;
          await deleteData("proccess.php", "board_permissions_V2", recordId);
          setBoardPermissions((prev) => prev.filter((p) => p.shared_with_id !== userId));
        }
      } else {
        const payload = {
          owner_id: currentUser.id,
          shared_with_id: userId,
          permission,
        };

        if (existing) {
          // Update
          const recordId = existing.Id || existing.id;
          await updateData("proccess.php", "board_permissions_V2", payload, recordId);
          setBoardPermissions((prev) =>
            prev.map((p) => (p.shared_with_id === userId ? { ...p, permission } : p))
          );
        } else {
          // Add
          const res = await addData("proccess.php", "board_permissions_V2", payload);
          if (res.success && res.data) {
            setBoardPermissions((prev) => [...prev, res.data]);
          } else {
            // Optimistic update
            setBoardPermissions((prev) => [...prev, { ...payload, id: Date.now().toString() }]);
          }
        }
      }
    } catch (err) {
      console.error("Error managing board permissions:", err);
      setError("Failed to persist board permission on the server.");
    }
  };

  const handleGoToSharedBoard = (userId) => {
    setAssigneeFilter(userId);
    setTabValue(0); // Switch back to Kanban
  };

  // ========================
  // Task Actions Callbacks
  // ========================
  const onTaskClick = (id) => {
    const task = tasks.find((t) => t.id === id);
    if (task) {
      setSelectedTask(task);
      setIsCreatingTask(false);
      setShowTaskDetail(true);
    }
  };

  const handleEditTask = (taskId, e) => {
    e.stopPropagation();
    const task = tasks.find((t) => t.id === taskId);
    if (task) {
      setSelectedTask(task);
      setIsCreatingTask(false);
      setShowTaskDetail(true);
    }
  };

  const handleDeleteTask = async (taskId, e) => {
    if (e) e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await deleteData("proccess.php", "tasks_V2", taskId);
        setTasks((prev) => prev.filter((t) => t.id !== taskId));
        setShowTaskDetail(false);
        setSelectedTask(null);
      } catch (err) {
        console.error("Delete task failed:", err);
        setError("Failed to delete task from database.");
      }
    }
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setIsCreatingTask(true);
    setShowTaskDetail(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      const payload = serializeTask(taskData);
      
      if (isCreatingTask) {
        // Create Task
        const response = await addData("proccess.php", "tasks_V2", payload);
        const newTask = parseTask(
          response.success && response.data ? response.data : { ...payload, id: Date.now().toString() },
          staffs
        );
        setTasks((prev) => [...prev, newTask]);
      } else {
        // Update Task
        await updateData("proccess.php", "tasks_V2", payload, taskData.id);
        const updatedTask = parseTask({ ...payload, id: taskData.id }, staffs);
        setTasks((prev) =>
          prev.map((t) => (t.id === taskData.id ? updatedTask : t))
        );
      }
      setShowTaskDetail(false);
      setSelectedTask(null);
    } catch (err) {
      console.error("Save task failed:", err);
      setError("Failed to save task in the database.");
    }
  };

  const handleDrop = async (taskId, newStatus) => {
    try {
      const taskObj = tasks.find((t) => t.id === taskId);
      if (!hasWriteAccess(taskObj)) {
        setError("Access Denied: You do not have permission to modify this task.");
        return;
      }

      // Optimistic update
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
      );

      // Save to server
      const updatedTask = { ...taskObj, status: newStatus };
      const payload = serializeTask(updatedTask);
      await updateData("proccess.php", "tasks_V2", payload, taskId);
    } catch (err) {
      console.error("Move task failed:", err);
      setError("Failed to save task position on the server.");
    }
  };

  const handleToggleComplete = async (taskId, isChecked) => {
    try {
      const taskObj = tasks.find((t) => t.id === taskId);
      if (!hasWriteAccess(taskObj)) {
        setError("Access Denied: You do not have permission to complete this task.");
        return;
      }

      const newStatus = isChecked ? "completed" : "todo";
      
      // Optimistic update
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
      );

      // Save to server
      const updatedTask = { ...taskObj, status: newStatus };
      const payload = serializeTask(updatedTask);
      await updateData("proccess.php", "tasks_V2", payload, taskId);
    } catch (err) {
      console.error("Toggle complete failed:", err);
      setError("Failed to toggle task completion on the server.");
    }
  };

  const handleMoveColumn = (fromIndex, toIndex) => {
    setColumns((prev) => {
      const newCols = [...prev];
      const [moved] = newCols.splice(fromIndex, 1);
      newCols.splice(toIndex, 0, moved);
      return newCols;
    });
  };

  const handleRemoveColumn = (index) => {
    if (columns.length > 1) {
      setColumns((prev) => prev.filter((_, i) => i !== index));
    } else {
      setError("Cannot remove the last board section.");
    }
  };

  const handleAddSection = () => {
    if (newSectionName.trim()) {
      const newId = newSectionName.toLowerCase().replace(/\s+/g, "_");
      setColumns((prev) => [...prev, { id: newId, label: newSectionName }]);
      setNewSectionName("");
      setShowAddSection(false);
    }
  };

  const handleRequestAccess = (name) => {
    setRequestFeedbackText(`Access request sent to ${name}.`);
    setTimeout(() => setRequestFeedbackText(""), 4000);
  };

  // ========================
  // Task Filtering Logic (Collaborator Aware)
  // ========================
  const getFilteredTasks = () => {
    let filtered = [...tasks];
    
    // Filter by Assignee / Collaborator
    if (assigneeFilter === "my") {
      filtered = filtered.filter(
        (t) =>
          t.assignee?.id === currentUser.id ||
          t.assignee?.name === "You" ||
          t.assignee?.name === currentUser.name ||
          (Array.isArray(t.collaborators) && t.collaborators.some((c) => c.id === currentUser.id))
      );
    } else if (assigneeFilter === "unassigned") {
      filtered = filtered.filter(
        (t) => t.assignee?.name === "Unassigned" || t.assignee?.id === "unassigned"
      );
    } else if (assigneeFilter !== "all") {
      // Viewing a specific user's board
      filtered = filtered.filter(
        (t) => t.assignee?.id === assigneeFilter || t.assignee?.name === assigneeFilter
      );
    }

    // Filter by Priority
    if (priorityFilter !== "all-priority") {
      filtered = filtered.filter((t) => t.priority === priorityFilter);
    }

    // Sort Tasks
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "due-date":
          return new Date(a.dueDate || "9999-12-31") - new Date(b.dueDate || "9999-12-31");
        case "priority": {
          const order = { high: 3, medium: 2, low: 1 };
          return (order[b.priority] || 0) - (order[a.priority] || 0);
        }
        case "created":
          return parseInt(b.id) - parseInt(a.id);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className="w-100 flex-raw" sx={{ minHeight: "100vh", display: "flex" }}>
        <AdvertSideBar />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          <TopAppBar />

          {/* Header */}
          <Box
            mb="10px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="100%"
            bgcolor={colors.dark[400]}
            height="11vh"
            sx={{ borderBottom: "1px solid rgba(0,0,0,0.05)", px: 3 }}
          >
            <Typography
              variant="h3"
              color={colors.blue[400]}
              fontWeight="bold"
              sx={{ m: 0, textAlign: "center" }}
            >
              Task Management
            </Typography>
          </Box>

          {/* Loading State */}
          {isLoading ? (
            <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
              <CircularProgress sx={{ color: "#6C5CE7" }} />
            </Box>
          ) : (
            <Box p="1rem" sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
              {/* Tabs */}
              <Box bgcolor="white" borderBottom="1px solid rgba(0,0,0,0.1)" borderRadius="8px 8px 0 0">
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  sx={{
                    minHeight: "unset",
                    px: 2,
                    "& .MuiTab-root": {
                      minHeight: 48,
                      textTransform: "none",
                      fontSize: 14,
                      color: "#717182",
                      "&.Mui-selected": { color: "#6C5CE7" },
                    },
                    "& .MuiTabs-indicator": { bgcolor: "#6C5CE7" },
                  }}
                >
                  <Tab icon={<ViewKanbanOutlined />} iconPosition="start" label="Kanban Board" />
                  <Tab icon={<ListAltOutlined />} iconPosition="start" label="List View" />
                  <Tab icon={<DashboardOutlined />} iconPosition="start" label="Dashboard" />
                  <Tab icon={<PeopleOutlined />} iconPosition="start" label="Team" />
                </Tabs>
              </Box>

              {/* TAB CONTENT */}

              {/* 1. Kanban Board & 2. List View Filter Bars */}
              {(tabValue === 0 || tabValue === 1) && (
                <Box
                  bgcolor={isBoardLocked ? "#F8FAFC" : "#f9fafb"}
                  borderRadius="0 0 8px 8px"
                  border="1px solid rgba(0,0,0,0.1)"
                  borderTop="none"
                  sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
                >
                  {/* Filter Bar */}
                  <Box
                    bgcolor="white"
                    borderBottom="1px solid rgba(0,0,0,0.1)"
                    px={3}
                    py={1.25}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                  >
                    <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={assigneeFilter}
                          onChange={(e) => setAssigneeFilter(e.target.value)}
                          sx={selectSx}
                        >
                          <MenuItem value="all">All Team Members</MenuItem>
                          <MenuItem value="my">My Tasks ({currentUser.name})</MenuItem>
                          <MenuItem value="unassigned">Unassigned Tasks</MenuItem>
                          <Divider sx={{ my: 0.5 }} />
                          {staffs.map((staff) => {
                            const u = mapStaffToUser(staff);
                            return (
                              <MenuItem key={u.id} value={u.id}>
                                Board: {u.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>

                      {!isBoardLocked && (
                        <>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={priorityFilter}
                              onChange={(e) => setPriorityFilter(e.target.value)}
                              sx={selectSx}
                            >
                              <MenuItem value="all-priority">All Priorities</MenuItem>
                              <MenuItem value="high">High</MenuItem>
                              <MenuItem value="medium">Medium</MenuItem>
                              <MenuItem value="low">Low</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                              sx={selectSx}
                            >
                              <MenuItem value="due-date">Due Date</MenuItem>
                              <MenuItem value="priority">Priority</MenuItem>
                              <MenuItem value="created">Created Date</MenuItem>
                            </Select>
                          </FormControl>
                        </>
                      )}
                    </Stack>

                    {/* Board Members Access Indicator */}
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Typography fontSize={12} fontWeight={600} color="#717182">
                        Board Access:
                      </Typography>
                      {assigneeFilter === "my" || assigneeFilter === "all" ? (
                        boardPermissions.length === 0 ? (
                          <Tooltip title="Only you have access to this board. Grant access in the Team tab.">
                            <Chip
                              label="Private"
                              size="small"
                              sx={{ bgcolor: "#F1F5F9", color: "#64748B", fontSize: 11, fontWeight: 500 }}
                            />
                          </Tooltip>
                        ) : (
                          <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 10 } }}>
                            <Tooltip title={`${currentUser.name} (Owner)`}>
                              <Avatar src={currentUser.image ? `https://mindsinaction.com.na/assets/media/pictures/${currentUser.image}` : undefined}>
                                {currentUser.avatar}
                              </Avatar>
                            </Tooltip>
                            {boardPermissions.map((perm) => {
                              const s = staffs.find((st) => st.Id === perm.shared_with_id);
                              if (!s) return null;
                              const u = mapStaffToUser(s);
                              const isEditor = perm.permission === "edit";
                              return (
                                <Tooltip key={u.id} title={`${u.name} (${isEditor ? "Editor" : "Viewer"})`}>
                                  <Avatar
                                    src={u.image ? `https://mindsinaction.com.na/assets/media/pictures/${u.image}` : undefined}
                                    sx={{ border: `2px solid ${isEditor ? "#10B981" : "#3B82F6"} !important` }}
                                  >
                                    {u.avatar}
                                  </Avatar>
                                </Tooltip>
                              );
                            })}
                          </AvatarGroup>
                        )
                      ) : (
                        (() => {
                          const boardOwnerStaff = staffs.find((s) => s.Id === assigneeFilter);
                          if (!boardOwnerStaff) return null;
                          const boardOwner = mapStaffToUser(boardOwnerStaff);
                          const isEditor = activePermission === "edit";
                          return (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Tooltip title={`${boardOwner.name} (Owner)`}>
                                <Avatar
                                  src={boardOwner.image ? `https://mindsinaction.com.na/assets/media/pictures/${boardOwner.image}` : undefined}
                                  sx={{ width: 24, height: 24, fontSize: 10 }}
                                >
                                  {boardOwner.avatar}
                                </Avatar>
                              </Tooltip>
                              <Chip
                                icon={isEditor ? <AdminPanelSettings sx={{ fontSize: "12px !important" }} /> : <Visibility sx={{ fontSize: "12px !important" }} />}
                                label={isBoardReadOnly ? "Viewer (Read-only)" : isBoardLocked ? "Locked" : "Editor (Full Access)"}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  bgcolor: isBoardReadOnly ? "#E8F0FE" : isBoardLocked ? "#FEE2E2" : "#E6F4EA",
                                  color: isBoardReadOnly ? "#3B82F6" : isBoardLocked ? "#EF4444" : "#10B981",
                                }}
                              />
                            </Stack>
                          );
                        })()
                      )}
                    </Stack>
                  </Box>

                  {/* Access Denied / Lock Screen */}
                  {isBoardLocked ? (
                    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, bgcolor: "#F8FAFC" }}>
                      <Box sx={{ p: 2, borderRadius: "50%", bgcolor: "#FEE2E2", color: "#EF4444", mb: 2 }}>
                        <Lock sx={{ fontSize: 36 }} />
                      </Box>
                      <Typography variant="h6" fontWeight={700} color="#1A1A24">
                        Task Board Locked
                      </Typography>
                      <Typography fontSize={13} color="#717182" sx={{ mt: 0.5, maxWidth: "320px", textAlign: "center" }}>
                        You do not have permission to view this task board. Contact the owner to request access.
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const owner = staffs.find((s) => s.Id === assigneeFilter);
                          if (owner) handleRequestAccess(`${owner.FirstName} ${owner.LastName}`);
                        }}
                        sx={{ mt: 3, textTransform: "none", bgcolor: "#6C5CE7", "&:hover": { bgcolor: "#5a4aa3" } }}
                      >
                        Request Access
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {/* Kanban View Content */}
                      {tabValue === 0 && (
                        <Box overflow="auto" p={3} sx={{ flex: 1 }}>
                          <Box display="flex" gap={3} minWidth="max-content">
                            {columns.map((col, index) => (
                              <KanbanColumn
                                key={col.id}
                                status={col.id}
                                label={col.label}
                                tasks={filteredTasks.filter((t) => t.status === col.id)}
                                onDrop={handleDrop}
                                onTaskClick={onTaskClick}
                                onAddTask={handleAddTask}
                                onEditTask={handleEditTask}
                                onDeleteTask={handleDeleteTask}
                                onToggleComplete={handleToggleComplete}
                                column={col}
                                columnIndex={index}
                                onRemoveColumn={handleRemoveColumn}
                                onMoveColumn={handleMoveColumn}
                                totalColumns={columns.length}
                                isReadOnly={isBoardReadOnly} // Enforce view permissions
                              />
                            ))}
                            
                            {!isBoardReadOnly && (
                              <Box flexShrink={0} width={200}>
                                <Button
                                  startIcon={<Add />}
                                  size="small"
                                  onClick={() => setShowAddSection(true)}
                                  sx={{
                                    width: "100%",
                                    justifyContent: "flex-start",
                                    color: "#717182",
                                    textTransform: "none",
                                    fontWeight: 400,
                                    fontSize: 13,
                                    borderRadius: "8px",
                                    "&:hover": { color: "#1a1a1a", bgcolor: "#F3F3F5" },
                                  }}
                                >
                                  Add Section
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}

                      {/* List View Content */}
                      {tabValue === 1 && (
                        <Box p={3} sx={{ flex: 1, overflowY: "auto" }}>
                          {filteredTasks.length === 0 ? (
                            <Box p={4} textAlign="center" bgcolor="#F8FAFC" borderRadius="12px" border="1px solid rgba(0,0,0,0.05)">
                              <Typography color="text.secondary" fontWeight={500}>
                                No tasks match your filters.
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ borderRadius: "12px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", bgcolor: "white" }}>
                              {/* Table Header */}
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "44px 1.7fr 1fr 0.8fr 1fr 1.4fr 120px",
                                  gap: 1,
                                  px: 2,
                                  py: 1.25,
                                  bgcolor: "#F8FAFC",
                                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                                }}
                              >
                                <Typography fontSize={12} fontWeight={700} color="#717182">Done</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Task</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Assignee</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Priority</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Due</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Progress</Typography>
                                <Typography fontSize={12} fontWeight={700} color="#717182">Actions</Typography>
                              </Box>

                              {/* Table Rows */}
                              <Box>
                                {filteredTasks.map((task) => {
                                  const priority = priorityConfig[task.priority] || { label: task.priority, color: "#717182", bg: "#ECECF0" };
                                  const totalSub = task.totalSubtasks ?? 0;
                                  const completedSub = task.completedSubtasks ?? 0;
                                  const progress = totalSub > 0 ? (completedSub / totalSub) * 100 : 0;
                                  const isOverdue = (() => {
                                    if (!task.dueDate || task.status === "completed") return false;
                                    const d = new Date(task.dueDate);
                                    const today = new Date();
                                    today.setHours(0,0,0,0);
                                    return d < today;
                                  })();

                                  const canWrite = hasWriteAccess(task);

                                  return (
                                    <Box
                                      key={task.id}
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: "44px 1.7fr 1fr 0.8fr 1fr 1.4fr 120px",
                                        gap: 1,
                                        px: 2,
                                        py: 1.25,
                                        alignItems: "center",
                                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "#F8FAFC" },
                                      }}
                                      onClick={() => onTaskClick(task.id)}
                                    >
                                      <Checkbox
                                        checked={task.status === "completed"}
                                        disabled={isBoardReadOnly}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleToggleComplete(task.id, e.target.checked);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        size="small"
                                        sx={{ p: 0, "&.Mui-checked": { color: "#6C5CE7" } }}
                                      />

                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          fontSize={13}
                                          fontWeight={600}
                                          sx={{
                                            lineHeight: 1.3,
                                            textDecoration: task.status === "completed" ? "line-through" : "none",
                                            color: task.status === "completed" ? "#717182" : "#1A1A24",
                                          }}
                                          noWrap
                                        >
                                          {task.title}
                                        </Typography>
                                        {task.collaborators?.length > 0 && (
                                          <Typography fontSize={11} color="#6C5CE7" fontWeight={500} sx={{ mt: 0.25 }}>
                                            Collaborator on board
                                          </Typography>
                                        )}
                                      </Box>

                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Avatar
                                          src={task.assignee?.image ? `https://mindsinaction.com.na/assets/media/pictures/${task.assignee.image}` : undefined}
                                          sx={{ width: 22, height: 22, bgcolor: "#6C5CE7", color: "white", fontSize: 10 }}
                                        >
                                          {task.assignee?.avatar}
                                        </Avatar>
                                        <Typography fontSize={12} color="#717182" noWrap>{task.assignee?.name}</Typography>
                                      </Box>

                                      <Chip
                                        label={priority.label}
                                        size="small"
                                        sx={{ bgcolor: priority.bg, color: priority.color, fontWeight: 600, height: 22, borderRadius: "6px" }}
                                      />

                                      <Typography fontSize={12} fontWeight={isOverdue ? 700 : 500} color={isOverdue ? "#EF4444" : "#717182"} noWrap>
                                        {task.dueDate ? `Due: ${new Date(task.dueDate).toLocaleDateString()}` : "No due date"}
                                      </Typography>

                                      <Box>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                          <Typography fontSize={11} color="#717182" fontWeight={600}>{completedSub}/{totalSub}</Typography>
                                          {totalSub > 0 && <Typography fontSize={11} color="#6C5CE7" fontWeight={700}>{Math.round(progress)}%</Typography>}
                                        </Stack>
                                        {totalSub > 0 ? (
                                          <LinearProgress variant="determinate" value={progress} sx={{ height: 4, borderRadius: 2, bgcolor: "#ECECF0", "& .MuiLinearProgress-bar": { bgcolor: "#6C5CE7" } }} />
                                        ) : (
                                          <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#ECECF0" }} />
                                        )}
                                      </Box>

                                      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" disabled={!canWrite} sx={{ color: "#717182" }} onClick={(e) => handleEditTask(task.id, e)}>
                                          <EditOutlined sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton size="small" disabled={!canWrite} sx={{ color: "#717182" }} onClick={(e) => handleDeleteTask(task.id, e)}>
                                          <DeleteOutlined sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {/* 3. Dashboard View Tab */}
              {tabValue === 2 && (
                <DashboardView tasks={tasks} currentUser={currentUser} />
              )}

              {/* 4. Team View Tab */}
              {tabValue === 3 && (
                <TeamView
                  staffsList={staffs}
                  boardPermissions={boardPermissions}
                  incomingPermissions={incomingPermissions}
                  currentUser={currentUser}
                  onGrantAccess={handleGrantAccess}
                  onGoToBoard={handleGoToSharedBoard}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Task Detail Slide-over Drawer */}
        {showTaskDetail && (
          <TaskDetailPanelMui
            task={selectedTask}
            isCreating={isCreatingTask}
            onClose={() => {
              setShowTaskDetail(false);
              setSelectedTask(null);
            }}
            onSave={handleSaveTask}
            onDelete={() => handleDeleteTask(selectedTask.id)}
            readOnly={selectedTask ? !hasWriteAccess(selectedTask) : false}
            staffsList={staffs}
          />
        )}

        {/* Add Section Dialog */}
        <Dialog open={showAddSection} onClose={() => setShowAddSection(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Create New Section</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Section Name"
              placeholder="e.g., Blocked, Testing"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              size="small"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddSection();
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddSection(false)}>Cancel</Button>
            <Button onClick={handleAddSection} variant="contained" disabled={!newSectionName.trim()}>
              Create Section
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mobile Detail Panel Overlay */}
        {showTaskDetail && (
          <Box
            onClick={() => {
              setShowTaskDetail(false);
              setSelectedTask(null);
            }}
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,0.5)",
              zIndex: 1200,
              display: { xs: "block", sm: "none" },
            }}
          />
        )}

        {/* Global Access Notification Banners */}
        {(error || requestFeedbackText) && (
          <Box
            sx={{
              position: "fixed",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 2000,
            }}
          >
            <Alert severity={error ? "error" : "success"} onClose={() => { setError(null); setRequestFeedbackText(""); }}>
              {error || requestFeedbackText}
            </Alert>
          </Box>
        )}
      </Box>
    </DndProvider>
  );
}